import {
  useWSSnackbar,
  WSActions,
  WSForm,
  WSInfoBox,
  WSInputText,
  WSInputTextarea,
  WSList,
  WSModal,
  WSModalCustomProps,
  WSPill,
  WSText
} from "@wingspanhq/fe-component-library";
import React, { useCallback } from "react";
import * as Yup from "yup";

import { WSErrorMessage } from "../../../../components/WSErrorMessage/WSErrorMessage";
import { WSQueries } from "../../../../query/WSQuery";
import { useMutationUpdateRequirementDefinition } from "../../../../query/requirementDefinitions/mutation/useMutationUpdateRequirementDefinition";
import { useQueryRequirementDefinition } from "../../../../query/requirementDefinitions/query/useQueryRequirementDefinition";
import { ErrorContextKey } from "../../../../services/platform";
import { getRequirementDefinitionTemplateByParams } from "../../utils/getRequirementDefinitionTemplateByParams";
import { getVersionNoteValidator } from "../../utils/validators";

type Props = {
  requirementDefinitionId: string;
} & WSModalCustomProps<boolean>;

export const EditRequirementDefinitionModal: React.FC<Props> = ({
  requirementDefinitionId,
  onClose
}) => {
  const { openSnackbar } = useWSSnackbar();
  const queryRequirement = useQueryRequirementDefinition(
    requirementDefinitionId
  );

  const [updateRequirement, updateRequirementMeta] =
    useMutationUpdateRequirementDefinition(requirementDefinitionId);

  const handleCancel = useCallback(() => {
    onClose(false);
  }, [onClose]);

  return (
    <WSModal
      shimmer={queryRequirement.isLoading}
      size="M"
      title="Edit requirement details"
      onClose={handleCancel}
    >
      <WSQueries queries={{ queryRequirement }}>
        {({ queryRequirementData }) => {
          const template = getRequirementDefinitionTemplateByParams({
            type: queryRequirementData.requirementType
          });

          const isNameEditable = !!template.isCustomName;
          const hasVersionNote =
            !!queryRequirementData.versionNote ||
            queryRequirementData.version > 1;

          return (
            <WSForm
              defaultValues={{
                description: queryRequirementData.description,
                name: queryRequirementData.name,
                versionNote: queryRequirementData.versionNote
              }}
              validationSchema={Yup.object().shape({
                description: Yup.string(),
                name: Yup.string().required("Name is required"),
                versionNote: getVersionNoteValidator(
                  queryRequirementData.version
                )
              })}
              onSubmit={async formData => {
                await updateRequirement(formData, {
                  onSuccess: async () => {
                    onClose(true);
                    openSnackbar({
                      message: "Requirement updated successfully",
                      type: "success"
                    });
                  }
                });
              }}
            >
              <WSList gap="XL">
                <WSForm.Field
                  component={WSInputText}
                  label="Name"
                  componentProps={{
                    disabled: !isNameEditable,
                    required: true,
                    helperText:
                      "The requirement name is visible to you and contractors",
                    pill: {
                      icon: "view",
                      text: "Public",
                      theme: "neutral"
                    }
                  }}
                  name="name"
                  placeholder="Requirement name"
                />

                <WSInfoBox title="Important note">
                  The name field is visible to contractors. We recommend
                  communicating with your contractors if the name change
                  significantly and may impact their requirement.
                </WSInfoBox>

                <WSForm.Field
                  component={WSInputTextarea}
                  componentProps={{
                    required: false,
                    pill: {
                      icon: "lock",
                      text: "Private",
                      theme: "neutral"
                    },
                    helperText:
                      "The description is visible internally to you and your team only"
                  }}
                  label="Description"
                  name="description"
                  placeholder="Add optional description for this requirement"
                />
                {hasVersionNote && (
                  <WSForm.Field
                    component={WSInputTextarea}
                    componentProps={{
                      helperText:
                        "Add context about why this version was created or what changed. This note is only visible to your team.",
                      pill: {
                        icon: "lock",
                        text: "Private",
                        theme: "neutral"
                      }
                    }}
                    label="Internal note"
                    name="versionNote"
                    placeholder="Add a note..."
                  />
                )}
                <WSErrorMessage
                  contextKey={ErrorContextKey.UpdateRequirement}
                  error={updateRequirementMeta.error}
                />

                <WSActions
                  alignment="fill"
                  buttons={[
                    {
                      kind: "Secondary",
                      label: "Cancel",
                      onClick: handleCancel,
                      type: "button"
                    },
                    {
                      kind: "Primary",
                      label: "Save changes",
                      loading: updateRequirementMeta.isLoading,
                      type: "submit"
                    }
                  ]}
                  mt="L"
                />
              </WSList>
            </WSForm>
          );
        }}
      </WSQueries>
    </WSModal>
  );
};
